import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState, useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { ConnectionCard } from '@/components/integrations';
import { useIntegrationStore } from '@/lib/integrations/store';
import * as api from '@/lib/integrations/api';
import type { UserConnection } from '@/lib/integrations/types';
import { Plus, Search, Link2, Grid3X3, List } from 'lucide-react';
import { toast } from 'sonner';

export const Route = createFileRoute('/_dashboard/integrations/')({
  component: IntegrationsPage,
});

type ViewMode = 'grid' | 'grouped';

function IntegrationsPage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [deletingAlias, setDeletingAlias] = useState<string | null>(null);
  const [settingDefaultAlias, setSettingDefaultAlias] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    if (typeof window !== 'undefined') {
      return (localStorage.getItem('integrations-view-mode') as ViewMode) || 'grid';
    }
    return 'grid';
  });

  const {
    connections,
    connectionsLoading,
    connectionsError,
    fetchConnections,
    removeConnection,
  } = useIntegrationStore();

  useEffect(() => {
    fetchConnections();
  }, [fetchConnections]);

  const filteredConnections = connections.filter(
    (conn) =>
      (conn.display_name?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
      conn.alias.toLowerCase().includes(searchQuery.toLowerCase()) ||
      conn.provider_config_key.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const connectionsByProvider = useMemo(() => {
    return filteredConnections.reduce((acc, conn) => {
      const key = conn.provider_config_key;
      if (!acc[key]) acc[key] = [];
      acc[key].push(conn);
      return acc;
    }, {} as Record<string, UserConnection[]>);
  }, [filteredConnections]);

  const handleViewModeChange = (mode: ViewMode) => {
    setViewMode(mode);
    if (typeof window !== 'undefined') {
      localStorage.setItem('integrations-view-mode', mode);
    }
  };

  const handleDelete = async (connection: UserConnection) => {
    if (!confirm(`Are you sure you want to delete the connection "${connection.display_name || connection.alias}"?`)) {
      return;
    }

    setDeletingAlias(connection.alias);
    try {
      await removeConnection(connection.alias);
      toast.success('Connection deleted successfully');
    } catch (error) {
      toast.error((error as Error).message || 'Failed to delete connection');
    } finally {
      setDeletingAlias(null);
    }
  };

  const handleSetDefault = async (connection: UserConnection) => {
    setSettingDefaultAlias(connection.alias);
    try {
      await api.setDefaultConnection(connection.alias);
      await fetchConnections();
      toast.success(`Set "${connection.display_name || connection.alias}" as default for ${connection.provider_config_key}`);
    } catch (error) {
      toast.error((error as Error).message || 'Failed to set default connection');
    } finally {
      setSettingDefaultAlias(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold">Integrations</h2>
          <p className="text-muted-foreground">Manage your connected data sources</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex border rounded-md">
            <Button
              variant={viewMode === 'grid' ? 'default' : 'ghost'}
              size="sm"
              className="rounded-r-none"
              onClick={() => handleViewModeChange('grid')}
            >
              <Grid3X3 className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === 'grouped' ? 'default' : 'ghost'}
              size="sm"
              className="rounded-l-none"
              onClick={() => handleViewModeChange('grouped')}
            >
              <List className="h-4 w-4" />
            </Button>
          </div>
          <Button onClick={() => navigate({ to: '/integrations/add' })}>
            <Plus className="mr-2 h-4 w-4" />
            Add Integration
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search connections..."
          className="pl-10"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {/* Error state */}
      {connectionsError && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-8">
            <p className="text-destructive mb-4">{connectionsError}</p>
            <Button variant="outline" onClick={() => fetchConnections()}>
              Try Again
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Loading state */}
      {connectionsLoading && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <CardContent className="flex items-center gap-4 py-4">
                <Skeleton className="size-12 rounded-full" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-3 w-24" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Connections Display */}
      {!connectionsLoading && !connectionsError && filteredConnections.length > 0 && (
        <>
          {viewMode === 'grid' ? (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {filteredConnections.map((connection) => (
                <ConnectionCard
                  key={connection.alias}
                  connection={connection}
                  onDelete={() => handleDelete(connection)}
                  onSetDefault={() => handleSetDefault(connection)}
                  deleting={deletingAlias === connection.alias}
                  settingDefault={settingDefaultAlias === connection.alias}
                />
              ))}
            </div>
          ) : (
            <div className="space-y-6">
              {Object.entries(connectionsByProvider)
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([provider, conns]) => (
                  <div key={provider} className="space-y-3">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold capitalize">
                        {provider.replace(/-/g, ' ')} ({conns.length})
                      </h3>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate({ to: '/integrations/add', search: { provider } })}
                      >
                        <Plus className="mr-1 h-3 w-3" />
                        Add another
                      </Button>
                    </div>
                    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                      {conns.map((connection) => (
                        <ConnectionCard
                          key={connection.alias}
                          connection={connection}
                          onDelete={() => handleDelete(connection)}
                          onSetDefault={() => handleSetDefault(connection)}
                          deleting={deletingAlias === connection.alias}
                          settingDefault={settingDefaultAlias === connection.alias}
                        />
                      ))}
                    </div>
                  </div>
                ))}
            </div>
          )}
        </>
      )}

      {/* Empty state */}
      {!connectionsLoading && !connectionsError && filteredConnections.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Link2 className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">No connections found</h3>
            <p className="text-muted-foreground text-center mb-4">
              {searchQuery
                ? 'Try adjusting your search query'
                : 'Get started by adding your first integration'}
            </p>
            {!searchQuery && (
              <Button onClick={() => navigate({ to: '/integrations/add' })}>
                <Plus className="mr-2 h-4 w-4" />
                Add Integration
              </Button>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
